import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntities } from '../api/entities';
import { useAppStore } from '../store';
import RiskGauge from '../components/visualization/RiskGauge';
import type { Entity, EntityType } from '../types';

const ENTITY_TYPES: EntityType[] = [
  'person', 'organization', 'location', 'vehicle', 'document',
  'financial_account', 'phone_number', 'email', 'ip_address', 'domain',
  'cryptocurrency_wallet', 'event',
];

const TYPE_BADGES: Record<string, string> = {
  person: 'bg-blue-500/20 text-blue-400',
  organization: 'bg-purple-500/20 text-purple-400',
  location: 'bg-green-500/20 text-green-400',
  vehicle: 'bg-orange-500/20 text-orange-400',
  document: 'bg-gray-500/20 text-gray-400',
  financial_account: 'bg-yellow-500/20 text-yellow-400',
  phone_number: 'bg-cyan-500/20 text-cyan-400',
  email: 'bg-teal-500/20 text-teal-400',
  ip_address: 'bg-red-500/20 text-red-400',
  domain: 'bg-orange-500/20 text-orange-400',
  cryptocurrency_wallet: 'bg-amber-500/20 text-amber-400',
  event: 'bg-violet-500/20 text-violet-400',
};

export default function EntityExplorer() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<EntityType | ''>('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);

  const navigate = useNavigate();
  const setGlobalEntity = useAppStore((s) => s.setSelectedEntity);

  const { data, isLoading } = useEntities({
    page,
    page_size: 20,
    search: search || undefined,
    type: typeFilter || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const entities = data?.items ?? [];
  const totalPages = data?.total_pages ?? 1;

  function handleSelect(entity: Entity) {
    setSelectedEntity(entity);
    setGlobalEntity(entity);
  }

  function handleSort(field: string) {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  }

  const riskColor = useMemo(() => {
    return (score: number) => {
      if (score >= 0.8) return 'text-red-400';
      if (score >= 0.6) return 'text-orange-400';
      if (score >= 0.4) return 'text-amber-400';
      return 'text-green-400';
    };
  }, []);

  return (
    <div className="flex h-full gap-6 animate-fade-in">
      {/* Main content */}
      <div className="flex-1 min-w-0 space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Entity Explorer</h1>
          <p className="text-sm text-gray-500 mt-1">
            {data?.total ?? 0} entities in database
          </p>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search entities..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-surface-tertiary border border-border-subtle rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/40"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as EntityType | '');
              setPage(1);
            }}
            className="px-3 py-2 bg-surface-tertiary border border-border-subtle rounded-lg text-sm text-gray-300 focus:outline-none focus:border-cyber-cyan/40"
          >
            <option value="">All Types</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace('_', ' ').replace(/^\w/, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="glass-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle">
                  {[
                    { key: 'name', label: 'Name' },
                    { key: 'type', label: 'Type' },
                    { key: 'risk_score', label: 'Risk' },
                    { key: 'confidence', label: 'Confidence' },
                    { key: 'updated_at', label: 'Updated' },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-300 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {sortBy === key && (
                          <svg className={`w-3 h-3 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                          </svg>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
                        Loading entities...
                      </div>
                    </td>
                  </tr>
                ) : entities.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                      No entities found
                    </td>
                  </tr>
                ) : (
                  entities.map((entity) => (
                    <tr
                      key={entity.id}
                      onClick={() => handleSelect(entity)}
                      onDoubleClick={() => navigate(`/entities/${entity.id}`)}
                      className={`cursor-pointer transition-colors hover:bg-surface-tertiary/50 ${
                        selectedEntity?.id === entity.id ? 'bg-helios-700/20' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${TYPE_BADGES[entity.type] || 'bg-gray-500/20 text-gray-400'}`}>
                            {entity.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-200">{entity.name}</p>
                            {entity.aliases.length > 0 && (
                              <p className="text-xs text-gray-500">aka {entity.aliases[0]}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`entity-badge ${TYPE_BADGES[entity.type] || 'bg-gray-500/20 text-gray-400'}`}>
                          {entity.type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-mono font-medium ${riskColor(entity.risk_score)}`}>
                          {Math.round(entity.risk_score * 100)}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-surface-primary rounded-full overflow-hidden">
                            <div
                              className="h-full bg-cyber-cyan rounded-full"
                              style={{ width: `${entity.confidence * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{Math.round(entity.confidence * 100)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {new Date(entity.updated_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle">
              <p className="text-xs text-gray-500">
                Page {page} of {totalPages} ({data?.total ?? 0} results)
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-xs rounded-md bg-surface-tertiary text-gray-400 disabled:opacity-40 hover:text-gray-200 transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-xs rounded-md bg-surface-tertiary text-gray-400 disabled:opacity-40 hover:text-gray-200 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Panel (slide-over) */}
      {selectedEntity && (
        <div className="w-80 flex-shrink-0 glass-panel p-4 space-y-4 overflow-y-auto animate-fade-in">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-300">Entity Details</h3>
            <button
              onClick={() => {
                setSelectedEntity(null);
                setGlobalEntity(null);
              }}
              className="p-1 rounded hover:bg-surface-tertiary text-gray-500 hover:text-gray-300"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex flex-col items-center">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold ${TYPE_BADGES[selectedEntity.type]}`}>
              {selectedEntity.name.charAt(0).toUpperCase()}
            </div>
            <h2 className="mt-3 text-lg font-semibold text-gray-100">{selectedEntity.name}</h2>
            <span className={`mt-1 entity-badge ${TYPE_BADGES[selectedEntity.type]}`}>
              {selectedEntity.type.replace('_', ' ')}
            </span>
          </div>

          <div className="flex justify-center">
            <RiskGauge score={selectedEntity.risk_score} size={100} label="Risk Score" />
          </div>

          {selectedEntity.description && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Description</p>
              <p className="text-sm text-gray-300">{selectedEntity.description}</p>
            </div>
          )}

          {selectedEntity.aliases.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Aliases</p>
              <div className="flex flex-wrap gap-1">
                {selectedEntity.aliases.map((alias) => (
                  <span key={alias} className="px-2 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded-full">
                    {alias}
                  </span>
                ))}
              </div>
            </div>
          )}

          {selectedEntity.tags.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Tags</p>
              <div className="flex flex-wrap gap-1">
                {selectedEntity.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Source</span>
              <span className="text-gray-300">{selectedEntity.source}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Confidence</span>
              <span className="text-gray-300">{Math.round(selectedEntity.confidence * 100)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">First Seen</span>
              <span className="text-gray-300">{new Date(selectedEntity.first_seen).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Last Seen</span>
              <span className="text-gray-300">{new Date(selectedEntity.last_seen).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Properties */}
          {Object.keys(selectedEntity.properties).length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Properties</p>
              <div className="space-y-1.5">
                {Object.entries(selectedEntity.properties).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-xs">
                    <span className="text-gray-500 capitalize">{key.replace('_', ' ')}</span>
                    <span className="text-gray-300 text-right max-w-[60%] truncate">
                      {String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-border-subtle">
            <button
              onClick={() => navigate(`/entities/${selectedEntity.id}`)}
              className="flex-1 px-3 py-2 text-xs font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors"
            >
              Profil complet
            </button>
            <button
              onClick={() => navigate(`/graph?entity=${selectedEntity.id}`)}
              className="flex-1 px-3 py-2 text-xs font-medium bg-surface-tertiary text-gray-400 rounded-lg hover:text-gray-200 transition-colors"
            >
              Voir dans Graph
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
